import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { getHighLevelContext } from '@/utils/highlevel';
import type { SyncHistoryEntry, SyncErrorDetail, SyncDetailsResponse } from '@/types';

interface UseSyncHistoryOptions {
  enabled?: boolean;
  limit?: number;
  entityType?: string;
  syncType?: 'manual' | 'auto' | 'webhook' | 'scheduled';
  status?: 'pending' | 'in_progress' | 'completed' | 'failed' | 'partial_success' | 'cancelled';
  refetchInterval?: number;
}

interface SyncHistoryResponse {
  success: boolean;
  data: SyncHistoryEntry[];
  meta: {
    count: number;
    limit: number;
  };
}

interface SyncErrorsResponse {
  success: boolean;
  data: SyncErrorDetail[];
  meta: {
    count: number;
  };
}

/**
 * Hook to fetch sync history from the API
 */
export function useSyncHistory(options: UseSyncHistoryOptions = {}) {
  const context = getHighLevelContext();
  const locationId = context?.locationId || '';

  const {
    enabled = true,
    limit = 20,
    entityType,
    syncType,
    status,
    refetchInterval,
  } = options;

  const query = useQuery<SyncHistoryResponse>({
    queryKey: ['sync-history', locationId, limit, entityType, syncType, status],
    queryFn: async () => {
      const params = new URLSearchParams({
        locationId,
        limit: String(limit),
      });

      if (entityType) params.append('entityType', entityType);
      if (syncType) params.append('syncType', syncType);
      if (status) params.append('status', status);

      const response = await apiClient.get(`/api/sync-history?${params.toString()}`);
      return response;
    },
    enabled: enabled && !!locationId,
    refetchInterval,
    staleTime: 10000, // 10 seconds
  });

  return {
    history: query.data?.data || [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook to fetch errors for a specific sync history entry
 */
export function useSyncHistoryErrors(syncHistoryId: string | null) {
  const context = getHighLevelContext();
  const locationId = context?.locationId || '';

  const query = useQuery<SyncErrorsResponse>({
    queryKey: ['sync-history-errors', syncHistoryId, locationId],
    queryFn: async () => {
      const response = await apiClient.get(
        `/api/sync-history/${syncHistoryId}/errors?locationId=${locationId}`
      );
      return response;
    },
    enabled: !!syncHistoryId && !!locationId,
    staleTime: 30000, // 30 seconds
  });

  return {
    errors: query.data?.data || [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

interface SyncDetailsApiResponse {
  success: boolean;
  data: SyncDetailsResponse;
  meta: {
    successCount: number;
    errorCount: number;
  };
}

/**
 * Hook to fetch sync details (successes and errors) for a specific sync history entry
 */
export function useSyncDetails(syncHistoryId: string | null) {
  const context = getHighLevelContext();
  const locationId = context?.locationId || '';

  const query = useQuery<SyncDetailsApiResponse>({
    queryKey: ['sync-history-details', syncHistoryId, locationId],
    queryFn: async () => {
      const response = await apiClient.get(
        `/api/sync-history/${syncHistoryId}/details?locationId=${locationId}`
      );
      return response;
    },
    enabled: !!syncHistoryId && !!locationId,
    staleTime: 30000, // 30 seconds
  });

  return {
    details: query.data?.data || null,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
